import React from 'react';
import { Palette, FileJson, Eraser, FileInput, Sparkles } from 'lucide-react';
import { C } from '../../constants/design-tokens';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { usePanels } from '../../shared/hooks/use-panels';
import { useEditorHost } from '../../shared/hooks/use-editor-host';
import { useMedia } from '../../media';
import { classifySubject } from '../../shaders/utils/classify-subject';
import { TabBtn } from '../../shared/ui/tab-btn';
import { ShapesButton } from '../../selection/components/shapes-button';
import { TextButton } from '../../selection/components/text-button';
import { UploadButton } from '../../media/components/upload-button';
import { BackgroundPanel } from '../../background/components/background-panel';
import { LoadJsonPanel } from '../../export/components/load-json-panel';
import { SoundPanel } from '../../sound-panel';
import { CreateGridCollectionButton } from '../../grouping';
import { TextOverlayButton } from '../../text/sobreimpreso/text-overlay-button';

type RightBarProps = {
  showLoadJson?: boolean;
};

const FADE_MASK = 'linear-gradient(to bottom, transparent 0, #000 12px, #000 calc(100% - 12px), transparent 100%)';

const Divider: React.FC = () => (
  <div style={{ width: '32px', height: '1px', background: C.border, margin: '4px 0', flexShrink: 0 }} />
);

/** Debug tools (JSON, Load, Delete) are only available in the dev environment. */
const isDevEnv = process.env.REACT_APP_ENV_SUFFIX === 'dev';

export const RightBar: React.FC<RightBarProps> = ({ showLoadJson = false }) => {
  const { canvas, clearCanvas, selectedObject } = useEditorCanvas();
  const { toggle, isOpen, close } = usePanels();
  const { getVideo } = useMedia();
  const host = useEditorHost();
  const showBackground = host !== 'avatars';

  const hasEffects = classifySubject(selectedObject, getVideo).kind !== 'none';

  React.useEffect(() => {
    if (!hasEffects && isOpen('effects')) close();
  }, [hasEffects, isOpen, close]);

  return (
    <div
      style={{
        width: '64px',
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: '16px',
        position: 'absolute',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        maxHeight: 'calc(100% - 32px)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        overflow: 'visible'
      }}
    >
      <div
        style={{
          scrollbarWidth: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '12px 0',
          gap: '4px',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'visible',
          WebkitMaskImage: FADE_MASK,
          maskImage: FADE_MASK
        }}
      >
        <ShapesButton />
        <TextButton />
        <TextOverlayButton />

        <Divider />
        {showBackground && (
          <TabBtn
            panelId='background'
            onClick={() => toggle('background')}
            label='Fondo'
            icon={<Palette size={18} />}
            active={isOpen('background')}
          />
        )}
        <UploadButton canvas={canvas} />
        <SoundPanel />
        <Divider />
        <CreateGridCollectionButton />
        {hasEffects && (
          <>
            <Divider />
            <TabBtn
              panelId='effects'
              onClick={() => toggle('effects')}
              label='Efectos'
              icon={<Sparkles size={18} />}
              active={isOpen('effects')}
            />
          </>
        )}
        {isDevEnv && (
          <>
            <Divider />
            <TabBtn
              panelId='json'
              onClick={() => toggle('json')}
              label='JSON'
              icon={<FileJson size={18} />}
              active={isOpen('json')}
            />
            {showLoadJson && (
              <TabBtn
                panelId='loadJson'
                onClick={() => toggle('loadJson')}
                label='Cargar'
                icon={<FileInput size={18} />}
                active={isOpen('loadJson')}
              />
            )}
            <TabBtn onClick={clearCanvas} label='Borrar' icon={<Eraser size={18} />} />
          </>
        )}
      </div>

      {showBackground && <BackgroundPanel />}
      {isDevEnv && showLoadJson && <LoadJsonPanel />}
    </div>
  );
};
